import { z } from 'zod';
import { uuidSchema } from '../primitives.js';

/**
 * Integration credentials — how another system proves it may write here.
 *
 * Distinct from everything in `auth`: that is a person signing in, with a
 * session that rotates, expires and belongs to somebody. This is a machine with
 * a long-lived secret, and the differences are what shape the contract below.
 *
 * ── The secret is returned once ────────────────────────────────────────────
 *
 * [[IssuedIntegrationCredential]] is the only type that carries it, and it is
 * produced by exactly two operations: creating a credential and rotating one.
 * Every read model below carries the public `keyId` and never the secret —
 * not for the owner who created it, not in an export, not in an audit row.
 *
 * A secret that can be displayed is a secret that leaks through a screen share
 * or a support ticket, and the person who issued it already had their chance to
 * copy it. If it is lost, rotate: that is cheaper than making it retrievable
 * forever so it can be read on the one day it is needed.
 *
 * ── The business is not in the contract ────────────────────────────────────
 *
 * Nothing a sender submits names a business. The credential does, and that is
 * the only thing that does — see `resolveIntegration` on the API side.
 */

/** What an owner sees about a credential they issued. */
export const integrationCredentialSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** The public half. Safe to display, log and quote in a support ticket. */
  keyId: z.string(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  rotatedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokedReason: z.string().nullable(),
  /** Null until it has authenticated once. The answer to "is it wired up yet". */
  lastUsedAt: z.string().nullable(),
  isActive: z.boolean(),
});
export type IntegrationCredential = z.infer<typeof integrationCredentialSchema>;

/**
 * A credential and its secret, at the one moment the secret exists in the open.
 *
 * `token` is the whole string a sender puts in its Authorization header. It is
 * assembled rather than stored: nothing persists it, and no later read can
 * reconstruct it.
 */
export const issuedIntegrationCredentialSchema = integrationCredentialSchema.extend({
  token: z.string(),
});
export type IssuedIntegrationCredential = z.infer<typeof issuedIntegrationCredentialSchema>;

export const createIntegrationCredentialSchema = z.object({
  /** What it is for, in words: "Ceno Hub dispatch". */
  name: z.string().trim().min(1).max(80),
});
export type CreateIntegrationCredentialInput = z.infer<
  typeof createIntegrationCredentialSchema
>;

/**
 * Revoking asks for a reason.
 *
 * A revoked credential stays in the table forever as the explanation for every
 * delivery it wrote, and "revoked" on its own explains nothing six months
 * later. The same argument as a cancelled delivery's reason.
 */
export const revokeIntegrationCredentialSchema = z.object({
  reason: z.string().trim().min(1).max(200),
});
export type RevokeIntegrationCredentialInput = z.infer<
  typeof revokeIntegrationCredentialSchema
>;

/**
 * A sending system reporting in.
 *
 * ── Why a sender has to say it is alive ────────────────────────────────────
 *
 * Ceno Hub's delivery sync was switched off for six days in October 2026 and
 * nothing in CenoX noticed, because nothing could: a sender that has stopped
 * looks exactly like a quiet week. Silence only means something when it is
 * measured against a promise to speak, so the sender now promises — a report
 * every minute or so while it is running — and CenoX treats the absence of one
 * as the alarm.
 *
 * The report is also the only way an owner can see what is STUCK on the
 * sender's side. CenoX can list the events it is holding; it cannot see the
 * ones that never left. Both halves are shown together on the health panel.
 *
 * Every field is bounded. This is stored verbatim beside the credential, and a
 * sender with a bug must not be able to turn that row into a megabyte.
 */
const isoInstant = z.string().datetime({ offset: true });

export const HEARTBEAT_MAX_ITEMS = 50;

export const integrationHeartbeatItemSchema = z.object({
  externalRef: z.string().trim().min(1).max(128),
  /** Something a person recognises — Ceno Hub's order code — never needed to be unique. */
  label: z.string().trim().max(64).nullable(),
  originSource: z.string().trim().min(1).max(32),
  version: z.number().int().positive(),
  eventType: z.string().trim().min(1).max(40),
  status: z.enum(['PENDING', 'FAILED']),
  attempts: z.number().int().min(0),
  lastError: z.string().max(300).nullable(),
  nextAttemptAt: isoInstant.nullable(),
  createdAt: isoInstant,
});
export type IntegrationHeartbeatItem = z.infer<typeof integrationHeartbeatItemSchema>;

export const integrationHeartbeatSchema = z.object({
  /** Which program is speaking, e.g. "cenohub-backend". Free text, for people. */
  sender: z.string().trim().min(1).max(64),
  /** False is a sender saying it is deliberately not sending. Still an alarm. */
  enabled: z.boolean(),
  reportedAt: isoInstant,
  outbox: z.object({
    pending: z.number().int().min(0),
    failed: z.number().int().min(0),
    oldestPendingAt: isoInstant.nullable(),
    lastSentAt: isoInstant.nullable(),
  }),
  backlog: z.object({
    /** Jobs that should be in CenoX and have not been queued yet. */
    eligibleUnsent: z.number().int().min(0),
    unmappedChannels: z.array(z.string().trim().min(1).max(32)).max(10),
  }),
  items: z.array(integrationHeartbeatItemSchema).max(HEARTBEAT_MAX_ITEMS),
});
export type IntegrationHeartbeat = z.infer<typeof integrationHeartbeatSchema>;

/**
 * How an integration is doing, in one word.
 *
 * Ordered by how urgently a person should look. A sender that has gone quiet
 * outranks one reporting failures, because a silent sender may be failing at
 * everything and saying nothing — the worse of the two.
 */
export const INTEGRATION_HEALTH_STATUSES = [
  'not_reporting',
  'silent',
  'failing',
  'delayed',
  'healthy',
] as const;
export type IntegrationHealthStatus = (typeof INTEGRATION_HEALTH_STATUSES)[number];

export const INTEGRATION_HEALTH_LABELS: Record<IntegrationHealthStatus, string> = {
  not_reporting: 'Not reporting',
  silent: 'Stopped',
  failing: 'Failing',
  delayed: 'Delayed',
  healthy: 'Healthy',
};

/** No report for this long and the sender is treated as stopped. */
export const HEARTBEAT_SILENT_AFTER_MS = 15 * 60 * 1000;
/** A queued event older than this is late enough to say so. */
export const PENDING_DELAYED_AFTER_MS = 10 * 60 * 1000;

export interface IntegrationHealthInput {
  lastHeartbeatAt: string | Date | null;
  report: IntegrationHeartbeat | null;
  heldCount: number;
  now: Date;
}

/**
 * The one place a status is decided.
 *
 * Pure, so the alert sweep, the owner's panel and the tests cannot disagree
 * about what "failing" means. `problems` is written for an owner, not an
 * engineer: each line says what is wrong in terms of their deliveries.
 */
export function classifyIntegrationHealth(input: IntegrationHealthInput): {
  status: IntegrationHealthStatus;
  problems: string[];
} {
  const { report, heldCount, now } = input;
  if (!input.lastHeartbeatAt) {
    return {
      status: 'not_reporting',
      problems: ['This integration has never reported its sync status.'],
    };
  }

  const problems: string[] = [];
  const last = new Date(input.lastHeartbeatAt).getTime();
  const quietFor = now.getTime() - last;
  const silent = quietFor > HEARTBEAT_SILENT_AFTER_MS || report?.enabled === false;
  if (report?.enabled === false) {
    problems.push('The sending system reports that sync is switched off. New deliveries are not being sent.');
  } else if (quietFor > HEARTBEAT_SILENT_AFTER_MS) {
    problems.push(
      `No report from the sending system for ${describeDuration(quietFor)}. Deliveries may not be arriving.`,
    );
  }

  const failed = report?.outbox.failed ?? 0;
  if (failed > 0) {
    problems.push(`${failed} event${failed === 1 ? ' was' : 's were'} rejected and will not be retried.`);
  }
  if (heldCount > 0) {
    problems.push(
      `${heldCount} event${heldCount === 1 ? ' is' : 's are'} held here waiting for an earlier one that has not arrived.`,
    );
  }

  const oldest = report?.outbox.oldestPendingAt ? new Date(report.outbox.oldestPendingAt).getTime() : null;
  const delayed = oldest !== null && now.getTime() - oldest > PENDING_DELAYED_AFTER_MS;
  if (delayed) {
    const pending = report?.outbox.pending ?? 0;
    problems.push(
      `${pending} event${pending === 1 ? ' has' : 's have'} been waiting to send for up to ${describeDuration(now.getTime() - oldest!)}.`,
    );
  }

  const unmapped = report?.backlog.unmappedChannels ?? [];
  if (unmapped.length > 0) {
    problems.push(`Channels with no CenoX location, so not sent: ${unmapped.join(', ')}.`);
  }

  const status: IntegrationHealthStatus = silent
    ? 'silent'
    : failed > 0 || heldCount > 0
      ? 'failing'
      : delayed
        ? 'delayed'
        : 'healthy';
  return { status, problems };
}

function describeDuration(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

/** One event CenoX is holding because an earlier version has not arrived. */
export interface HeldIntegrationEvent {
  externalRef: string;
  originSource: string;
  version: number;
  receivedAt: string;
}

export interface IntegrationHealth {
  credentialId: string;
  name: string;
  status: IntegrationHealthStatus;
  problems: string[];
  lastHeartbeatAt: string | null;
  lastEventAt: string | null;
  report: IntegrationHeartbeat | null;
  held: { count: number; items: HeldIntegrationEvent[] };
}
